import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ParticularOrderDto } from 'src/generated_client/model/particularOrderDto';
import { WaypointStatus } from 'src/generated_client/model/waypointStatus';
import { WaypointCompletionTime } from 'src/generated_client/model/waypointCompletionTime';
import { ParticularOrderService } from 'src/generated_client/api/particular-order.service';
import {
  WhereToPay,
  WhereToPayLabel,
} from 'src/generated_client/model/whereToPay';
import { HOUR_SUFFIX } from 'src/app/shared/constants/time-text.constants';

@Component({
  selector: 'app-view-order',
  templateUrl: './view-order.component.html',
  styleUrls: ['./view-order.component.css'],
})
export class ViewOrderComponent implements OnInit {
  readonly hourSuffix = HOUR_SUFFIX;

  waypointStatusLabelMap: { [key: number]: string } = {
    0: 'Pendiente',
    1: 'En direccion',
    2: 'Completado',
    3: 'Cancelado'
  };

  statusLabelMap: { [key: number]: string } = {
    0: 'Nuevo',
    1: 'Tarifado',
    2: 'Confirmado',
    3: 'Asignado',
    4: 'Programado',
    5: 'En Curso',
    6: 'Finalizado',
  };

  waypointTimesMap: { [key: number]: number } = {};
  sortedWaypoints: any[] = [];

  constructor(
    public dialogRef: MatDialogRef<ViewOrderComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { order: ParticularOrderDto },
    private particularOrderService: ParticularOrderService
  ) {}

  ngOnInit(): void {
    this.sortedWaypoints = this.sortWaypoints(this.data.order.waypoints);

    if (this.data.order.id) {
      this.loadWaypointCompletionTimes(this.data.order.id);
    }
  }

  private sortWaypoints(waypoints?: any[]): any[] {
    const safeWaypoints = waypoints || [];
    return [...safeWaypoints].sort(
      (a, b) => this.getWaypointOrderIndex(a) - this.getWaypointOrderIndex(b)
    );
  }

  getWaypointOrderIndex(waypoint: any): number {
    if (waypoint && waypoint.orderIndex !== undefined && waypoint.orderIndex !== null) {
      return Number(waypoint.orderIndex);
    }

    // Backward compatibility if backend still sends "order" in some responses.
    if (waypoint && waypoint.order !== undefined && waypoint.order !== null) {
      const legacyOrder = Number(waypoint.order);
      return legacyOrder > 0 ? legacyOrder - 1 : legacyOrder;
    }

    return 0;
  }

  getWaypointDisplayNumber(waypoint: any, fallbackIndex: number): number {
    const index = this.getWaypointOrderIndex(waypoint);
    return (index >= 0 ? index : fallbackIndex) + 1;
  }

  getOriginAddress(): string {
    if (!this.sortedWaypoints.length) {
      return '-';
    }

    return this.sortedWaypoints[0]?.address || '-';
  }

  getOriginAddressDetail(): string {
    if (!this.sortedWaypoints.length) {
      return '';
    }

    return this.sortedWaypoints[0]?.addressDetail || '';
  }

  getDestinationAddress(): string {
    if (!this.sortedWaypoints.length) {
      return '-';
    }

    return this.sortedWaypoints[this.sortedWaypoints.length - 1]?.address || '-';
  }

  getDestinationAddressDetail(): string {
    if (!this.sortedWaypoints.length) {
      return '';
    }

    return this.sortedWaypoints[this.sortedWaypoints.length - 1]?.addressDetail || '';
  }

  private loadWaypointCompletionTimes(orderId: number): void {
    this.particularOrderService.getWaypointCompletionTimesByParticularOrder(orderId)
      .subscribe({
        next: (waypointTimes: WaypointCompletionTime[]) => {
          // Crear mapa de waypointId -> timeInMinutes
          this.waypointTimesMap = {};
          waypointTimes.forEach(wt => {
            this.waypointTimesMap[wt.waypointId] = wt.timeInMinutes;
          });
        },
        error: (error) => {
          console.error('Error loading waypoint completion times:', error);
        }
      });
  }

  getWaypointTime(waypointId?: number): number | null {
    if (!waypointId || this.waypointTimesMap[waypointId] === undefined) {
      return null;
    }
    return this.waypointTimesMap[waypointId];
  }

  getStatusLabel(status: number): string {
    return this.statusLabelMap[status] || String(status);
  }

  getWhereToPayLabel(whereToPay: number): string {
    return WhereToPayLabel[whereToPay as WhereToPay] || String(whereToPay);
  }

  getWaypointStatusLabel(status: number): string {
    return this.waypointStatusLabelMap[status] || String(status);
  }

  close(): void {
    this.dialogRef.close();
  }

  formatDate(date: any): string {
    if (!date) return '';

    // Handle the date string - if it doesn't end with Z, we'll treat it as UTC
    const dateStr =
      typeof date === 'string' && !date.endsWith('Z') ? `${date}Z` : date;
    const d = new Date(dateStr);

    // Format in Argentina time (UTC-3)
    return new Intl.DateTimeFormat('es-AR', {
      timeZone: 'America/Argentina/Buenos_Aires',
      year: '2-digit',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(d);
  }

  formatDateOnly(date: any): string {
    if (!date) return '';

    // Handle the date string - if it doesn't end with Z, we'll treat it as UTC
    const dateStr =
      typeof date === 'string' && !date.endsWith('Z') ? `${date}Z` : date;
    const d = new Date(dateStr);

    // Format only date without time
    return new Intl.DateTimeFormat('es-AR', {
      timeZone: 'America/Argentina/Buenos_Aires',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(d);
  }

  formatTimeRange(timeRange?: string | null): string {
    if (!timeRange) return '-';

    const escapedHourSuffix = this.escapeRegExp(this.hourSuffix);
    const hourRegex = new RegExp(
      `(\\d{1,2}(?::\\d{1,2})?)(?!\\s*${escapedHourSuffix})`,
      'gi'
    );

    return timeRange.replace(hourRegex, `$1 ${this.hourSuffix}`);
  }

  private escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
}
